import { MOBILE_TAG_HEIGHT, Tag, TagSize, type TagVariant } from '../Tag.js';
import { OverflowMenu } from './components/OverflowMenu.js';
import { type TagMenuItem } from './types.js';
import { deriveContainerHeight } from './utils.js';
import { useToggle } from '@contra/react-hooks/useToggle';
import {
  Children,
  cloneElement,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

const MENU_CONTAINER_OFFSET = 50;

//  ---- CUSTOM STYLES -----------------
const Container = styled.div`
  display: block;
  overflow: visible;
`;

// BEFORE REACHING into this component, please consider
// styling the Tag component from your container component.
// This can be done with ${StyledTagBox} { ... }.
const TagItems = styled.span<{
  buttonSize: number;
  gap: number;
  maxHeight: number;
  menuContainerOffset: number;
}>`
  ${({ gap, maxHeight, menuContainerOffset }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${gap}px;
    overflow: hidden;
    max-height: ${maxHeight}px;

    // we have special classes that the cloneElement
    // applies based on if a tag is visible or not. This is so we can sort items
    // visible then menu, then hidden items.
    .last-visible,
    .visible {
      order: 0;
    }

    .last-visible {
      margin-right: ${menuContainerOffset}px;
    }

    .hidden {
      order: 2;
      // We need to make sure an item marked as hidden is wrapped
      // onto the next line, otherwise it gets stuck in a hide/show loop
      margin-left: 100px;
    }
  `}
`;

const MenuContainer = styled.span<{ menuContainerOffset: number }>`
  ${({ menuContainerOffset }) => css`
    order: 1;
    display: inline-flex;
    align-items: center;
    margin-left: -${menuContainerOffset}px;
  `}
`;

type TagItemObserverProps = {
  readonly children: ReactElement | ReactElement[];
  readonly customItems?: ReactElement[];
  readonly disableOverflowMenu?: boolean;
  readonly gap: number;
  readonly hideOverflowMenu: boolean;
  readonly isOverflowMenuOpen: boolean;
  readonly itemHeight: number;
  readonly maxHeight: number;
  readonly menuContainerOffset: number;
  readonly overFlowMenuPlacement?: 'bottom' | 'left' | 'right' | 'top';
  readonly overflowMenuPositionStrategy: 'absolute' | 'fixed';
  readonly tagItems: TagMenuItem[];
  readonly tagSize: TagSize;
  readonly toggleOverflowMenuOpen: VoidFunction;
  readonly variant: TagVariant;
};

const TagItemObserver = ({
  children,
  customItems,
  disableOverflowMenu = false,
  gap,
  hideOverflowMenu,
  isOverflowMenuOpen,
  itemHeight,
  maxHeight,
  menuContainerOffset,
  overFlowMenuPlacement,
  overflowMenuPositionStrategy,
  tagItems,
  tagSize,
  toggleOverflowMenuOpen,
  variant,
}: TagItemObserverProps) => {
  const itemWrapperRef = useRef<HTMLSpanElement>(null);

  const [visibilityMap, setVisibilityMap] = useState<{
    [key: string]: boolean;
  }>({});

  // need to useEffect because of the cleanup
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const updatedEntries: { [key: string]: boolean } = {};
      for (const entry of entries) {
        const targetName = entry.target.attributes.getNamedItem('name')?.value;
        if (!targetName) continue;
        if (entry.isIntersecting) {
          updatedEntries[targetName] = true;
        } else {
          updatedEntries[targetName] = false;
        }
      }

      setVisibilityMap((previous) => ({
        ...previous,
        ...updatedEntries,
      }));
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: itemWrapperRef.current,
      threshold: 1,
    });

    const childItems = Array.from(itemWrapperRef.current?.children ?? []);
    for (const child of childItems) {
      if (child.attributes.getNamedItem('name')) {
        observer.observe(child);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [children]);

  const tags = useMemo(() => {
    return [...(customItems ?? []), ...tagItems];
  }, [customItems, tagItems]);

  const visibleItemNames = useMemo(() => {
    return tags
      .filter((tag) => {
        const value = 'value' in tag ? tag.value : (tag.key ?? '');
        const isVisible = visibilityMap[value] ?? false;
        return isVisible;
      })
      .map((tag) => {
        return 'value' in tag ? tag.value : (tag.key ?? '');
      });
  }, [tags, visibilityMap]);

  // we need to convert the hidden item names to the actual tag items
  const overFlowItems = useMemo(() => {
    return tagItems.filter((tagItem) => {
      const isVisible = visibilityMap[tagItem.value];
      return visibilityMap[tagItem.value] !== undefined && !isVisible;
    });
  }, [tagItems, visibilityMap]);

  const buttonSize = itemHeight;
  const showOverflowMenu =
    overFlowItems && overFlowItems.length > 0 && !hideOverflowMenu;

  const tagsToRender = useMemo(() => {
    return Children.map(children, (child) => {
      const key = child.key;

      // we need to just add some visible css
      // classes so we can adjust the flex order
      // and adjust the margin of the last item to allow for the overflow menu
      const isLastVisibleItem =
        visibleItemNames[visibleItemNames.length - 1] === key;
      const isVisible = visibleItemNames.includes(String(key));
      const adjustForOverflowMenu = isLastVisibleItem && showOverflowMenu;
      // Checks the itemWrappedRef being mounted before applying any classes
      const targetClassName = itemWrapperRef.current
        ? adjustForOverflowMenu
          ? 'last-visible'
          : isVisible
            ? 'visible'
            : 'hidden'
        : '';

      return cloneElement(child, {
        className: targetClassName,
        // we are adding a custom attribute to the tag
        // so we can use this to build the visibility map
        name: key,
      });
    });
  }, [children, showOverflowMenu, visibleItemNames]);

  return (
    <TagItems
      buttonSize={buttonSize}
      gap={gap}
      maxHeight={maxHeight}
      menuContainerOffset={menuContainerOffset}
      ref={itemWrapperRef}
    >
      {tagsToRender}
      {showOverflowMenu ? (
        <>
          {disableOverflowMenu ? (
            <Tag
              label={`+${overFlowItems.length}`}
              size={tagSize}
              variant={variant}
            />
          ) : (
            <MenuContainer menuContainerOffset={menuContainerOffset}>
              <OverflowMenu
                buttonSize={buttonSize}
                isOpen={isOverflowMenuOpen}
                items={overFlowItems}
                placement={overFlowMenuPlacement}
                strategy={overflowMenuPositionStrategy}
                tagSize={tagSize}
                toggleMenu={toggleOverflowMenuOpen}
                variant={variant}
              />
            </MenuContainer>
          )}
        </>
      ) : null}
    </TagItems>
  );
};

type TagListMenuProps<TItem> = {
  readonly ariaLabel?: string;
  readonly customItems?: ReactElement[];
  readonly disableOverflowMenu?: boolean;
  readonly gap?: number;
  readonly hideOverflowMenu?: boolean;
  readonly iconPlacement?: 'left' | 'right';
  readonly itemHeight?: number;
  readonly items: readonly TItem[];
  readonly maxRows: number;
  readonly menu?: ReactElement;
  readonly menuContainerOffset?: number;
  readonly overFlowMenuPlacement?: 'bottom' | 'left' | 'right' | 'top';
  readonly overflowMenuPositionStrategy?: 'absolute' | 'fixed';
  readonly readOnly?: boolean;
  readonly showHoverState?: boolean;
  readonly tagSize?: TagSize;
  readonly transformItemToTag: (item: TItem) => TagMenuItem;
  readonly variant?: TagVariant;
};

export const TagListMenu = <TItem extends object>({
  ariaLabel = 'Tag List Menu',
  customItems,
  disableOverflowMenu,
  gap = 8,
  hideOverflowMenu = false,
  iconPlacement = 'left',
  itemHeight = MOBILE_TAG_HEIGHT,
  items,
  maxRows,
  menuContainerOffset = MENU_CONTAINER_OFFSET,
  overFlowMenuPlacement,
  overflowMenuPositionStrategy = 'absolute',
  readOnly = false,
  showHoverState = false,
  tagSize = 'default',
  transformItemToTag,
  variant = 'outline',
}: TagListMenuProps<TItem>) => {
  const realItemHeight = tagSize === 'small' ? 24 : itemHeight;
  // state
  const [isOverflowMenuOpen, toggleOverflowMenuOpen] = useToggle(false);

  // data transform
  const containerHeight = useMemo(
    () => deriveContainerHeight({ gap, itemHeight: realItemHeight, maxRows }),
    [gap, maxRows, realItemHeight],
  );

  const tagItems = useMemo(() => {
    return items.map(transformItemToTag);
  }, [items, transformItemToTag]);

  return (
    <Container
      aria-label={ariaLabel}
      role="region"
    >
      <TagItemObserver
        customItems={customItems}
        disableOverflowMenu={disableOverflowMenu}
        gap={gap}
        hideOverflowMenu={hideOverflowMenu}
        isOverflowMenuOpen={isOverflowMenuOpen}
        itemHeight={realItemHeight}
        maxHeight={containerHeight}
        menuContainerOffset={menuContainerOffset}
        overFlowMenuPlacement={overFlowMenuPlacement}
        overflowMenuPositionStrategy={overflowMenuPositionStrategy}
        tagItems={tagItems}
        tagSize={tagSize}
        toggleOverflowMenuOpen={toggleOverflowMenuOpen}
        variant={variant}
      >
        {[
          ...(customItems ?? []),
          ...tagItems.map(
            ({
              href,
              icon,
              label,
              mode,
              onClick,
              tooltip,
              value,
              variant: tagVariant,
            }) => {
              return (
                <Tag
                  ariaLabel={label}
                  href={href}
                  icon={icon}
                  iconPlacement={iconPlacement}
                  key={value}
                  label={label}
                  mode={mode}
                  onClick={onClick}
                  readOnly={readOnly}
                  showHoverState={showHoverState}
                  size={tagSize}
                  tooltip={tooltip}
                  value={value}
                  variant={tagVariant ?? variant}
                />
              );
            },
          ),
        ]}
      </TagItemObserver>
    </Container>
  );
};
